/**
 * Tier progression — owns the rules for when a child's effective
 * `ageGroup` should advance early via achievement vs strictly via birthdate.
 *
 * Design (see CoursesPage chapter for context):
 *   - `child.ageGroup` is the EFFECTIVE tier (what content the kid sees).
 *   - Floor = `ageGroupFromBirthDate(birthDate)`. The effective tier can
 *     never be below the calendar floor — birthdays catch achievement
 *     stragglers up.
 *   - Ceiling = `'upper-elementary'`. We have no curriculum past that.
 *   - In `'achievement'` mode (default), graduating ALL tier-gated courses
 *     at the current tier triggers an early bump to the next tier.
 *
 * Everything below is pure logic — no React, no Zustand reads. Callers
 * (childStore, courseStore side-effects, CoursesPage) inject the data and
 * apply the returned mutations.
 */
import type { AgeGroup, Child, TierAdvancement } from '../types/child';
import { ageGroupFromBirthDate } from '../utils/date';

const TIER_ORDER: AgeGroup[] = ['pre-k', 'kinder', 'early-elementary', 'upper-elementary'];

export function tierIndex(tier: AgeGroup): number {
  return TIER_ORDER.indexOf(tier);
}

export function nextTier(current: AgeGroup): AgeGroup | null {
  const idx = tierIndex(current);
  return idx >= 0 && idx < TIER_ORDER.length - 1 ? TIER_ORDER[idx + 1] : null;
}

export function maxTier(a: AgeGroup, b: AgeGroup): AgeGroup {
  return tierIndex(a) >= tierIndex(b) ? a : b;
}

/**
 * Apply the birthdate floor: returns the higher of `child.ageGroup` and the
 * tier their birthdate puts them in. Use this when a session boots so a
 * kid who aged up while the app was closed catches up automatically.
 */
export function catchUpToAge(child: Child): AgeGroup {
  return maxTier(child.ageGroup, ageGroupFromBirthDate(child.birthDate));
}

export interface TierAdvanceCheck {
  shouldAdvance: boolean;
  fromTier: AgeGroup;
  toTier: AgeGroup | null;
  totalCourses: number;
  graduatedAtCurrentTier: number;
  /** Reasons we did NOT advance (mode disabled, already at top, etc.). */
  reason?: 'mode-disabled' | 'at-ceiling' | 'incomplete';
}

/**
 * Pure check: should this child auto-advance right now? Caller supplies
 * the list of tier-gated course ids and the predicate that says "this
 * child has graduated this course at this tier."
 *
 * Pass `graduatedAtTier` so the check is tier-aware — a kinder graduation
 * doesn't count when the child is now at early-elementary.
 */
export function checkTierAdvance(
  child: Child,
  tierGatedCourseIds: readonly string[],
  graduatedAtTier: (courseId: string, tier: AgeGroup) => boolean,
): TierAdvanceCheck {
  const mode = child.tierProgressionMode ?? 'achievement';
  const fromTier = child.ageGroup;
  const target = nextTier(fromTier);
  const total = tierGatedCourseIds.length;

  if (mode !== 'achievement') {
    return { shouldAdvance: false, fromTier, toTier: target, totalCourses: total, graduatedAtCurrentTier: 0, reason: 'mode-disabled' };
  }
  if (!target) {
    return { shouldAdvance: false, fromTier, toTier: null, totalCourses: total, graduatedAtCurrentTier: total, reason: 'at-ceiling' };
  }

  let done = 0;
  for (const id of tierGatedCourseIds) {
    if (graduatedAtTier(id, fromTier)) done++;
  }

  return {
    shouldAdvance: done >= total && total > 0,
    fromTier,
    toTier: target,
    totalCourses: total,
    graduatedAtCurrentTier: done,
    reason: done >= total ? undefined : 'incomplete',
  };
}

export function buildAdvancementEntry(
  toTier: AgeGroup,
  trigger: TierAdvancement['trigger'],
): TierAdvancement {
  return { tier: toTier, advancedAt: new Date().toISOString(), trigger };
}
